/** @module Auth0UtilsPureJs */
/* eslint-disable import/extensions */
import Auth0Tenant from './auth0-tenant-utils.mjs';
import Auth0Domain from './auth0-domain-utils.mjs';
import Auth0Constants from './auth0-constants.mjs';

class Auth0UtilsPureJs {
  static get isBrowser() {
    return typeof window !== 'undefined';
  }

  static get isNodeJs() {
    return typeof process === 'object';
  }

  static hasProp = (obj, prop) => {
    return !!(obj && Object.prototype.hasOwnProperty.call(obj, prop) && obj[prop]);
  };

  static urlIndexOf = (string, urlPart) => {
    return string.toLowerCase().indexOf(urlPart.toLowerCase());
  };

  static isUrlForV3 = (url) => {
    return !url
      ? false
      // Matches ship*/v3* (standard) and local.(shipstation|sslocal).com (local dev)
      : url.match(/^http[s]?:\/\/(ship|v3|local\.(shipstation|sslocal)\.com)/ig);
  };

  static isUrlForV2 = (url) => {
    return !url
      ? false
      : url.match(/^http[s]?:\/\/(ss|v2)/ig);
  };

  static isUrlForSsLite = (url) => {
    return !url
      ? false
      // Matches simple*/lite* (SS Lite subdomains in all environments)
      : url.match(/^http[s]?:\/\/(simple|lite)/ig);
  };

  static isUrlForSsApp = (url) => {
    return Auth0UtilsPureJs.isUrlForV2(url) || Auth0UtilsPureJs.isUrlForV3(url) || Auth0UtilsPureJs.isUrlForSsLite(url);
  };

  static isString = (o) => {
    return o && typeof o === 'string';
  };

  static isArray = (o) => {
    return o && Array.isArray(o);
  };

  static urlReplace = (string, urlPartFind, urlPartReplace) => {
    if (!string || !urlPartFind || !urlPartReplace) {
      return string;
    }
    // Case-insensitive match on urlPartFind, but preserve the case of the rest of the string.
    // The previous implementation lowercased the entire string which corrupted camelCase query
    // params (e.g. ss.external.app JSON keys) during post-login redirect URL construction.
    const idx = string.toLowerCase().indexOf(urlPartFind.toLowerCase());
    if (idx === -1) return string;
    return string.slice(0, idx) + urlPartReplace.toLowerCase() + string.slice(idx + urlPartFind.length);
  };

  static getUserClaim = (claim, claimSet) => {
    if (!claimSet) {
      return null;
    }
    return claimSet[`${Auth0Constants.AUTH0_CLAIM_NAMESPACE}${claim}`];
  };

  static getUserClaimSet = (claimSet) => {
    if (!claimSet) {
      return null;
    }
    const normalizedClaims = {};
    Object.keys(claimSet).forEach((claimKey) => {
      const claimName = claimKey.replace(Auth0Constants.AUTH0_CLAIM_NAMESPACE, '');
      normalizedClaims[claimName] = claimSet[claimKey];
    });
    return normalizedClaims;
  };

  static getTopLevelDomain = (hostnameArg) => {
    const hostname = hostnameArg instanceof URL ? hostnameArg.hostname : hostnameArg;
    const regex = /(?:[^.]+\.)?([^.\s]+\.[^.]+)$/;
    const match = hostname.match(regex);
    if (match) {
      return match[1];
    }
    throw new Error(`hostname ${hostname} is not valid. hostname must be a string of the format "subdomain.domain.com" or a URL object`);
  };

  /**
   * @function
   * @static
   * @description A simple internal helper function that will check if a user's (custom ShipStation) roleset contains the "system (super) admin" flag
   * @param {object} req The standard expressjs request object involved in the Auth0 action
   * @returns {string} The value of the claim or falsy val if not present
   */
  static isSysAdmin = (user) => {
    const userRoleMask = Auth0UtilsPureJs.getUserClaim('role_set', user);
    if (!userRoleMask) {
      return false;
    }
    // eslint-disable-next-line
    return (2048 & userRoleMask) === 2048;
  };

  static getUrlHostname = (url) => {
    try {
      return new URL(url).hostname;
    } catch (e) {
      console.log(`URL hostname: could not parse ${url}`);
    }
  };

  static isHostnameForV3 = (hostname) => {
    return (
      Auth0UtilsPureJs.urlIndexOf(hostname, 'ship-dev') === 0
      || Auth0UtilsPureJs.urlIndexOf(hostname, 'v3-dev') === 0
      || Auth0UtilsPureJs.urlIndexOf(hostname, 'ship') === 0
      // local dev hostnames (local.shipstation.com, local.sslocal.com) are v3 equivalents
      || Auth0UtilsPureJs.urlIndexOf(hostname, 'local.') === 0
    );
  };

  static buildQueryString = (data) => {
    return new URLSearchParams(data).toString();
  };

  static parseQueryString = (queryString = null) => {
    return new URLSearchParams(queryString);
  };

  static tenantOrDomainResolver = (tenantOrDomain) => {
    const resolved = {};
    if (typeof tenantOrDomain === 'string') {
      if (Auth0Tenant.isTenant(tenantOrDomain)) {
        resolved.tenant = new Auth0Tenant(tenantOrDomain);
        resolved.domain = new Auth0Domain(resolved.tenant.resolveDomain());
      } else {
        // is domain string
        resolved.domain = new Auth0Domain(tenantOrDomain);
        resolved.tenant = new Auth0Tenant(resolved.domain.resolveTenant());
      }
    } else if (tenantOrDomain instanceof Auth0Tenant) {
      resolved.tenant = tenantOrDomain;
      resolved.domain = new Auth0Domain(resolved.tenant.resolveDomain());
    } else if (tenantOrDomain instanceof Auth0Domain) {
      resolved.domain = tenantOrDomain;
      resolved.tenant = new Auth0Tenant(resolved.domain.resolveTenant());
    } else {
      throw new Error(`Invalid tenant or domain => ${tenantOrDomain || 'empty'}`);
    }

    return resolved;
  };

  static escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  static findFirstMatch = (obj, searchValue, path = []) => {
    // eslint-disable-next-line no-restricted-syntax
    for (const key in obj) {
      // eslint-disable-next-line no-prototype-builtins
      if (obj.hasOwnProperty(key)) {
        const currentPath = path.concat(key);
        if (obj[key] === searchValue) {
          return currentPath;
        }
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          const result = Auth0UtilsPureJs.findFirstMatch(obj[key], searchValue, currentPath);
          if (result) {
            return result;
          }
        }
      }
    }
    return null;
  };

  static base64Decode = (str, parseJson = false) => {
    const decoded = Auth0UtilsPureJs.isBrowser
      ? window.decodeURIComponent(window.escape(window.atob(str)))
      : Buffer.from(str, 'base64').toString('utf8');
    return parseJson
      ? JSON.parse(decoded)
      : decoded;
  };

  static base64UrlDecode = (str, parseJson = false) => {
    // the encoding used by JWT segments
    const pureBase64 = str.replace(/-/g, '+').replace(/_/g, '/');
    return Auth0UtilsPureJs.base64Decode(pureBase64, parseJson);
  };

  static base64Encode = (toEncode) => {
    toEncode = typeof toEncode === 'string'
      ? toEncode
      : JSON.stringify(toEncode);
    return Auth0UtilsPureJs.isBrowser
      ? window.btoa(window.unescape(window.encodeURIComponent(toEncode)))
      : Buffer.from(toEncode, 'utf8').toString('base64');
  };

  static getJwtBodyFromToken = (jwt, parseBody = true) => {
    return Auth0UtilsPureJs.base64UrlDecode(jwt.split('.')[1], parseBody);
  };

  static getNormalizedJwtBodyFromToken = (jwt, parseBody = true) => {
    const jwtBody = Auth0UtilsPureJs.base64UrlDecode(jwt.split('.')[1], parseBody);
    const normalizedClaims = {};
    Object.keys(jwtBody).forEach((claimKey) => {
      const claimName = claimKey.replace(Auth0Constants.AUTH0_CLAIM_NAMESPACE, '');
      normalizedClaims[claimName] = jwtBody[claimKey];
    });
    return normalizedClaims;
  };

  static getJwtUserDataFromToken = (jwt) => {
    const claimExclusions = [
      'aud', 'exp', 'iat', 'iss', 'nonce', 'sid',
    ];
    const allClaims = Auth0UtilsPureJs.getJwtBodyFromToken(jwt);
    const userKeys = Object.keys(allClaims).filter(c => !claimExclusions.some(e => e === c));
    const userData = {};
    userKeys.forEach(uk => userData[uk] = allClaims[uk]);

    return userData;
  };

  static getJwtHeaderFromToken = (jwt, parseBody = true) => {
    return Auth0UtilsPureJs.base64UrlDecode(jwt.split('.')[0], parseBody);
  };

  static getDecodedJwtPartsFromToken = (jwt, parse = false) => {
    const jwtParts = jwt.split('.');
    return {
      header: Auth0UtilsPureJs.getJwtHeaderFromToken(jwt, parse),
      payload: Auth0UtilsPureJs.getJwtBodyFromToken(jwt, parse),
      signature: jwtParts[2],
    };
  };

  static getEncodedJwtPartsFromToken = (jwt) => {
    const jwtParts = jwt.split('.');
    return {
      header: jwtParts[0],
      payload: jwtParts[1],
      signature: jwtParts[2],
    };
  };

  static getTldDomain = (domain) => {
    const domainParts = domain.split('.').slice(-2);
    return domainParts.join('.');
  };

  static errorToString = (error) => {
    if (error === null || error === undefined) {
      return String(error);
    }

    switch (typeof error) {
      case 'string':
      case 'number':
      case 'boolean':
        return String(error);

      case 'object':
        try {
          // For objects, attempt to stringify. Handle arrays, errors, and plain objects.
          if (Array.isArray(error)) {
            return `Array: [${error.map(this.errorToString).join(', ')}]`;
          } if (error instanceof Error) {
            return `Error: ${error.name} - ${error.message}\nStack: ${error.stack}`;
          }
          return `Object: ${JSON.stringify(error, null, 2)}`;
        } catch (jsonError) {
          // Handle cases where JSON.stringify fails (e.g., circular references)
          return `Object: [Cannot stringify - ${jsonError.message}]`;
        }

      case 'function':
        return `Function: ${error.name || 'anonymous'}`;

      case 'symbol':
        return `Symbol: ${error.toString()}`;

      default:
        return `Unknown type: ${String(error)}`;
    }
  };

  static sleep = (ms) => {
    return new Promise(resolve => setTimeout(resolve, ms));
  };

  static _getEmailMaskPart = (emailPart, numVisibleCharacters = 4) => {
    const partLength = emailPart.length;
    return partLength > numVisibleCharacters
      ? `${emailPart.substring(0, numVisibleCharacters)}${'*'.repeat(partLength - numVisibleCharacters)}`
      : '*'.repeat(numVisibleCharacters);
  };

  static getMaskedEmailAddressDisplayString = (email) => {
    // this is the same method Auth0 uses to mask email addresses for MFA factors
    const emailParts = email.split('@');
    const maskedNamePart = this._getEmailMaskPart(emailParts[0]);
    const maskedDomainPart = this._getEmailMaskPart(emailParts[1]);
    return `${maskedNamePart}@${maskedDomainPart}`;
  };

  static getMaskedUsernameDisplayString = (username) => {
    const len = username.length;

    if (len === 1) return username;
    if (len === 2) return `${username[0]}*`;
    if (len >= 3 && len <= 4) {
      // Keep the first character visible, mask the rest
      return username[0] + '*'.repeat(len - 1);
    }

    // For 5 or more characters: Keep the first and last visible, mask the middle
    return username[0] + '*'.repeat(len - 2) + username[len - 1];
  };

  static addSearchParamToUrl = (url, key, value) => {
    const urlObj = new URL(url);
    urlObj.searchParams.set(key, value);
    return urlObj.toString();
  };

  /**
   * Remove one or more search parameters from a URL
   * @param {string} url - The URL to strip parameters from
   * @param {string|string[]} params - Parameter name(s) to remove
   * @param {string} [baseUrl] - Optional base URL for relative URLs (defaults to 'http://placeholder' for parsing)
   * @returns {string} The URL with specified parameters removed, or the original URL if parsing fails
   */
  static stripSearchParams = (url, params, baseUrl = 'http://placeholder') => {
    if (!url) {
      return url;
    }

    try {
      const paramsToStrip = Array.isArray(params) ? params : [params];
      const urlObj = new URL(url, baseUrl);

      paramsToStrip.forEach((param) => {
        urlObj.searchParams.delete(param);
      });

      // If we used a placeholder base, return just the path + search + hash
      if (baseUrl === 'http://placeholder' && !url.startsWith('http')) {
        return `${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
      }

      return urlObj.toString();
    } catch (error) {
      console.warn('stripSearchParams: Failed to parse URL, returning original:', error.message);
      return url;
    }
  };

  static decodeUsernameFromEncodedEmail(encodedUsernameEmail) {
    console.log('decodeUsernameFromEncodedEmail', encodedUsernameEmail);
    if (!encodedUsernameEmail.startsWith(`${Auth0Constants.FAKE_USERNAME_EMAIL_LOCAL_PART}@`)) {
      return encodedUsernameEmail;
    }
    const illegalCharReplacementMap = Auth0Constants.ILLEGAL_EMAIL_ADDRESS_CHAR_MAPPING;
    // strategy to hide a username within a valid email address is to base64 encode it and use it as the domain part
    // which can be up to 255 characters in length. The local part of an address must be less than 64 characters
    // so it's best to use the domain for hiding due to its larger capacity
    const domainWithoutExtension = /(?<=@)(.*?)(?=\.[^.]+$)/.exec(encodedUsernameEmail)[1];

    if (!domainWithoutExtension) {
      return encodedUsernameEmail;
    }

    const encodedUsername = domainWithoutExtension;

    // Any literal . chars were inserted artificially to split domain in 63 char segments
    // just just remove all of them to get the original base64 encoded username
    let encodedUsernameClean = encodedUsername.replace(/\./g, '');

    // restore any base64 symbols replaced with plain text surrogates for the 3 illegal base64 characters
    Object.keys(illegalCharReplacementMap).forEach((key) => {
      const value = illegalCharReplacementMap[key];
      const regex = new RegExp(value, 'g');
      encodedUsernameClean = encodedUsernameClean.replace(regex, key);
    });

    return Auth0UtilsPureJs.base64Decode(encodedUsernameClean);
  }
}

export default Auth0UtilsPureJs;
